import type { DriftQueueFilter } from '@interfaces/driftQueue';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { useThemeTokens } from '@providers/themeTokenProvider';
import type { FC, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseDriftQueueCounts } from './useDriftQueue';

export type DriftQueueStatCardsProps = {
  activeFilter: DriftQueueFilter;
  onFilterChange: (next: DriftQueueFilter) => void;
  counts: UseDriftQueueCounts;
};

type StatCardDef = {
  filter: DriftQueueFilter;
  labelKey: string;
  count: number;
  /** sys.color token for active card solid background */
  activeBg: string | undefined;
  /** sys.color token for active card text */
  activeText: string | undefined;
  /** sys.color token for inactive card tinted background */
  inactiveBg: string | undefined;
  /** sys.color token for inactive card text */
  inactiveText: string | undefined;
  /** sys.color token for the count number when active */
  countText: string | undefined;
};

export const DriftQueueStatCards: FC<DriftQueueStatCardsProps> = ({
  activeFilter,
  onFilterChange,
  counts,
}) => {
  const { t } = useTranslation(['driftQueue']);
  const { sys } = useThemeTokens();

  const cards: StatCardDef[] = [
    {
      filter: 'needsAction',
      labelKey: 'driftQueue:tabs.needsAction',
      count: counts.needsAction,
      activeBg: sys.color.primary,
      activeText: sys.color.onPrimary,
      inactiveBg: sys.color.primaryVariant,
      inactiveText: sys.color.onPrimaryVariant,
      countText: sys.color.onPrimary,
    },
    {
      filter: 'compoundBreach',
      labelKey: 'driftQueue:tabs.compoundBreach',
      count: counts.compoundBreach,
      activeBg: sys.color.negative,
      activeText: sys.color.onNegative,
      inactiveBg: sys.color.negativeVariant,
      inactiveText: sys.color.onNegativeVariant,
      countText: sys.color.onNegative,
    },
    {
      filter: 'singleBreach',
      labelKey: 'driftQueue:tabs.singleBreach',
      count: counts.singleBreach,
      activeBg: sys.color.warning,
      activeText: sys.color.onWarning,
      inactiveBg: sys.color.warningVariant,
      inactiveText: sys.color.onWarningVariant,
      countText: sys.color.onWarning,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        mb: 1.5,
        flexWrap: 'wrap',
      }}
    >
      {cards.map((card) => {
        const isActive = activeFilter === card.filter;
        // `activeBg` holds the strong tone colour, `inactiveBg` the light tint.
        const accent = card.activeBg;
        const tint = card.inactiveBg;

        return (
          <Box
            key={card.filter}
            role='button'
            tabIndex={0}
            onClick={() => onFilterChange(card.filter)}
            onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onFilterChange(card.filter);
              }
            }}
            aria-pressed={isActive}
            sx={{
              flex: '1 1 110px',
              minWidth: 100,
              px: 1,
              py: 0.5,
              borderRadius: 2,
              // White surface (active = faint tone tint) with a tone-coloured
              // left accent bar instead of a full block of colour.
              backgroundColor: isActive ? tint : sys.color.surface,
              border: `1px solid ${isActive ? accent : sys.color.outlineVariant}`,
              borderLeft: `3px solid ${accent}`,
              cursor: 'pointer',
              transition: 'background-color 0.15s, border-color 0.15s',
              '&:hover': {
                backgroundColor: tint,
                borderColor: accent,
                borderLeft: `3px solid ${accent}`,
              },
            }}
          >
            <Typography
              variant='labelSmall'
              sx={{
                color: sys.color.onSurfaceVariant,
                display: 'block',
                fontWeight: isActive ? 700 : 600,
              }}
            >
              {t(card.labelKey)}
            </Typography>
            <Typography
              variant='headingMedium'
              sx={{
                color: accent,
                fontWeight: 700,
                lineHeight: 1.1,
                fontSize: '1.25rem',
              }}
            >
              {card.count}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
